/**
 * i18n engine (§15.1 — multi-language increment 1, flag `multi_language`).
 *
 * Design (owner-approved plan, 2026-07-27):
 *  - NATURAL KEYS (gettext style): `t('Tickets')` — the English string is the
 *    key. English needs no dictionary; a missing translation renders the
 *    English text, so English output is byte-identical whether or not i18n is
 *    healthy, and a partial dictionary can never blank a screen.
 *  - Separators are disabled so keys may contain '.', ':' and '&' freely.
 *  - Dictionaries lazy-load: `src/locales/<code>.json` is fetched only when a
 *    user actually switches to that language (English users download nothing).
 *  - Init is SYNCHRONOUS (initImmediate: false + no async plugins), so t() is
 *    safe from the very first render — no flash of raw keys.
 *  - Locale resolution (applyLocaleForMe): tenant flag OFF → always English;
 *    flag ON → user's saved locale if supported → English. (Tenant-default and
 *    browser-language steps join in a later increment when tenant settings
 *    carry a language.)
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LANGUAGE, isSupportedLanguage } from '@/lib/languages';
/** The per-tenant feature flag gating all language switching. */
export const MULTI_LANGUAGE_FLAG = 'multi_language';
i18n.use(initReactI18next).init({
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    resources: { [DEFAULT_LANGUAGE]: { translation: {} } },
    // Natural keys: never treat '.'/':' in a string as nesting/namespace.
    keySeparator: false,
    nsSeparator: false,
    interpolation: { escapeValue: false }, // React already escapes
    initImmediate: false, // synchronous init — t() valid from first render
    returnEmptyString: false, // an untranslated ""-entry falls back to English
});
const loaded = new Set([DEFAULT_LANGUAGE]);
/** Load a dictionary (once) and switch the interface to it. */
export async function applyLocale(code) {
    const target = isSupportedLanguage(code) ? code : DEFAULT_LANGUAGE;
    if (!loaded.has(target)) {
        try {
            const bundle = await import(`../locales/${target}.json`);
            i18n.addResourceBundle(target, 'translation', bundle.default ?? bundle, true, true);
            loaded.add(target);
        }
        catch {
            // Missing/broken dictionary → stay on English rather than break the UI.
            if (i18n.language !== DEFAULT_LANGUAGE)
                await i18n.changeLanguage(DEFAULT_LANGUAGE);
            return;
        }
    }
    if (i18n.language !== target)
        await i18n.changeLanguage(target);
}
/**
 * Resolve + apply the interface language for the signed-in user.
 * Called whenever /me lands (login, reload, profile update). Flag OFF (or no
 * session) pins English — today's behavior, guaranteed.
 */
export function applyLocaleForMe(me) {
    const flagOn = !!me?.feature_flags?.[MULTI_LANGUAGE_FLAG];
    const code = flagOn && isSupportedLanguage(me?.user.locale) ? me.user.locale : DEFAULT_LANGUAGE;
    void applyLocale(code);
}
/**
 * Module-level translate for page/component code (increment 2+).
 *
 * Natural keys make this safe to call anywhere — outside React, in constants
 * computed at render, in helpers. Re-render on language switch is guaranteed
 * globally: AppShell keys the routed content by `i18n.language`, remounting
 * every page when the language changes (switching is rare, so a remount is
 * the industry-standard retrofit trade-off vs threading useTranslation
 * through hundreds of components).
 */
export function t(key, options) {
    return i18n.t(key, options ?? {});
}
export default i18n;
